import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import BugReportIcon from "@mui/icons-material/BugReport";
import pestReportService from "../../../api/pestReportService";
import { getViewUrl } from "../../../api/filesService";

/* ─── Tokens ──────────────────────────────────────────────────── */
const T = {
  blue:       "#1B6EF3",
  blueBg:     "#EEF4FF",
  blueMid:    "#DBEAFE",
  text:       "#111827",
  textSub:    "#6B7280",
  textHint:   "#9CA3AF",
  border:     "#E5E7EB",
  borderFocus:"#D1D5DB",
  bg:         "#FFFFFF",
  bgPage:     "#F9FAFB",
  levels: {
    low:      { color: "#16A34A", bg: "#F0FDF4", label: "Nulo" },
    medium:   { color: "#D97706", bg: "#FFFBEB", label: "Bajo" },
    high:     { color: "#EA580C", bg: "#FFF7ED", label: "Medio" },
    critical: { color: "#DC2626", bg: "#FEF2F2", label: "Crítico" },
  },
};

/* ─── Helpers ─────────────────────────────────────────────────── */
const getLevel = (lvl) => {
  if (lvl === 0) return T.levels.low;
  if (lvl === 1) return T.levels.medium;
  if (lvl === 2) return T.levels.high;
  return T.levels.critical;
};

const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-MX", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const calcDuration = (entry, exit) => {
  if (!entry || !exit) return "—";
  const mins = Math.floor((new Date(exit) - new Date(entry)) / 60000);
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

/* ─── Sub-components ──────────────────────────────────────────── */
const MetaItem = ({ label, value }) => (
  <Box sx={{ flex: 1 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 500, color: T.textHint, textTransform: "uppercase", letterSpacing: "0.07em", mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: T.text }}>
      {value}
    </Typography>
  </Box>
);

const Divider = () => (
  <Box sx={{ width: "1px", height: 32, background: T.border, alignSelf: "center" }} />
);

/* ─── Main component ──────────────────────────────────────────── */
export const ViewPestReportModal = ({ open, service, onClose }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewUrl, setViewUrl] = useState(null);
  const [pdfFullscreen, setPdfFullscreen] = useState(false);

  useEffect(() => {
    if (open && service) loadReport();
  }, [open, service]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      setReport(null);
      setViewUrl(null);
      const reports = await pestReportService.getReportsByEventId(service._id || service.id);
      if (!reports.length) { setError("No hay reporte de plagas para este servicio"); return; }
      const pestReport = reports[0];
      setReport(pestReport);
      if (pestReport.reportPdfKey) {
        const { url } = await getViewUrl(pestReport.reportPdfKey);
        setViewUrl(url);
      }
    } catch {
      setReport(null); setViewUrl(null); setError("Error al cargar el reporte");
    } finally {
      setLoading(false);
    }
  };

  const lv = report ? getLevel(report.pestLevel) : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
            border: `1px solid ${T.border}`,
            boxShadow: "0 8px 40px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
            background: T.bg,
            height: "90vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
          "& .MuiBackdrop-root": {
            background: "rgba(17,24,39,0.45)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2, flexShrink: 0, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{
              width: 34, height: 34, borderRadius: "8px",
              background: T.blueBg,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <BugReportIcon sx={{ fontSize: 18, color: T.blue }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.3 }}>
                {service?.branch?.name || "—"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: T.textSub }}>
                {service?.type}
              </Typography>
            </Box>
          </Stack>

          <Button
            onClick={onClose}
            sx={{
              minWidth: 0, width: 32, height: 32, p: 0,
              borderRadius: "8px",
              color: T.textSub,
              "&:hover": { background: T.bgPage, color: T.text },
            }}
          >
            <CloseIcon sx={{ fontSize: 17 }} />
          </Button>
        </Box>

        {/* ── Content ──────────────────────────────────────────── */}
        <DialogContent
          sx={{
            flex: 1, display: "flex", flexDirection: "column",
            gap: 2, px: 3, py: 2.5, overflow: "hidden",
          }}
        >
          {error && (
            <Alert severity="error" sx={{ borderRadius: "8px", fontSize: 13 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress size={28} thickness={3} sx={{ color: T.blue }} />
            </Box>
          ) : report ? (
            <>
              {/* Meta strip */}
              <Box sx={{
                display: "flex", alignItems: "stretch",
                px: 2.5, py: 2,
                borderRadius: "10px",
                background: T.bgPage,
                border: `1px solid ${T.border}`,
                gap: 2,
              }}>
                <MetaItem label="Entrada" value={fmt(report.entryTime)} />
                <Divider />
                <MetaItem label="Salida" value={fmt(report.exitTime)} />
                <Divider />
                <MetaItem label="Duración" value={calcDuration(report.entryTime, report.exitTime)} />
              </Box>

              {/* Pest level */}
              <Box sx={{
                display: "flex", alignItems: "center", gap: 2,
                px: 2.5, py: 2,
                borderRadius: "10px",
                background: lv.bg,
                border: `1px solid ${lv.color}22`,
              }}>
                {/* Score badge */}
                <Box sx={{
                  width: 48, height: 48, borderRadius: "10px",
                  background: "#fff",
                  border: `1px solid ${lv.color}33`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, color: lv.color, lineHeight: 1 }}>
                    {Math.round(report.pestLevel)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: lv.color }}>
                      {lv.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: T.textSub }}>
                      — nivel de infestación
                    </Typography>
                  </Stack>
                  {/* Progress bar */}
                  <Box sx={{ height: 4, borderRadius: 2, background: `${lv.color}22`, overflow: "hidden" }}>
                    <Box sx={{
                      height: "100%", borderRadius: 2,
                      width: `${Math.min(100, (report.pestLevel / 4) * 100)}%`,
                      background: lv.color,
                      transition: "width 0.6s ease",
                    }} />
                  </Box>
                </Box>
              </Box>

              {/* PDF viewer */}
              {viewUrl && (
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, borderRadius: "10px", border: `1px solid ${T.border}`, overflow: "hidden" }}>
                  {/* PDF toolbar */}
                  <Box sx={{
                    px: 2, py: 1.25,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    borderBottom: `1px solid ${T.border}`,
                    background: T.bgPage,
                    flexShrink: 0,
                  }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.textSub }}>
                      Reporte PDF
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setPdfFullscreen(true)}
                      startIcon={<FullscreenIcon sx={{ fontSize: "14px !important" }} />}
                      sx={{
                        fontSize: 12, textTransform: "none", fontWeight: 500,
                        color: T.blue, px: 1.25, py: 0.5, borderRadius: "6px",
                        "&:hover": { background: T.blueBg },
                      }}
                    >
                      Expandir
                    </Button>
                  </Box>
                  <Box sx={{ flex: 1, overflow: "hidden" }}>
                    <iframe
                      src={viewUrl}
                      style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                      title="PDF"
                    />
                  </Box>
                </Box>
              )}
            </>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
          <Button
            onClick={onClose}
            sx={{
              fontSize: 13, fontWeight: 500, textTransform: "none",
              color: T.text, px: 2.5, py: 0.875, borderRadius: "8px",
              border: `1px solid ${T.border}`,
              "&:hover": { background: T.bgPage, borderColor: T.borderFocus },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Fullscreen PDF ──────────────────────────────────────── */}
      <Dialog
        open={pdfFullscreen}
        onClose={() => setPdfFullscreen(false)}
        fullScreen
        sx={{
          "& .MuiDialog-paper": { background: T.bg, overflow: "hidden" },
          "& .MuiBackdrop-root": { backdropFilter: "blur(4px)", background: "rgba(17,24,39,0.45)" },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{
            px: 3, py: 1.75,
            display: "flex", justifyContent: "space-between", alignItems: "center",
            borderBottom: `1px solid ${T.border}`,
            background: T.bg,
            flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text }}>
              Reporte de plagas
            </Typography>
            <Button
              onClick={() => setPdfFullscreen(false)}
              startIcon={<CloseIcon sx={{ fontSize: "15px !important" }} />}
              sx={{
                fontSize: 13, textTransform: "none", fontWeight: 500,
                color: T.textSub, px: 2, py: 0.75, borderRadius: "8px",
                border: `1px solid ${T.border}`,
                "&:hover": { background: T.bgPage, color: T.text },
              }}
            >
              Cerrar
            </Button>
          </Box>
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <iframe src={viewUrl} style={{ width: "100%", height: "100%", border: "none" }} title="PDF Full" />
          </Box>
        </Box>
      </Dialog>
    </>
  );
};